import React, { useState, useEffect } from 'react';
import { createRecurringInvoice, fetchStudents, fetchFees, fetchDiscounts, fetchRecurringInvoiceIntervals } from '../utils/api';
import { mapErrorMessage } from '../utils/errorMapping';
import './_Buttons.css';
import './_Input.css';
import './_Modal.css';

const CreateRecurringInvoiceModal = ({ closeModal, refreshData }) => {
    const [studentId, setStudentId] = useState('');
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [firstInvoiceDate, setFirstInvoiceDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [intervalType, setIntervalType] = useState('');
    const [interval, setInterval] = useState('');
    const [dueDays, setDueDays] = useState('');
    const [fees, setFees] = useState([]);
    const [discounts, setDiscounts] = useState([]);
    const [paymentLink, setPaymentLink] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [loading, setLoading] = useState(true);

    const [availableStudents, setAvailableStudents] = useState([]);
    const [availableFees, setAvailableFees] = useState([]);
    const [availableDiscounts, setAvailableDiscounts] = useState([]);
    const [availableIntervals, setAvailableIntervals] = useState([]);
    
    const [selectedFee, setSelectedFee] = useState('');
    const [selectedDiscount, setSelectedDiscount] = useState('');

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                const [studentsResponse, feesRes, discountsRes, intervalsRes] = await Promise.all([
                    fetchStudents({ limit: 150, page: 1, main_status: 3 }),
                    fetchFees(),
                    fetchDiscounts(),
                    fetchRecurringInvoiceIntervals()
                ]);
                setAvailableStudents(studentsResponse.data || []);
                setAvailableFees(feesRes.data || []);
                setAvailableDiscounts(discountsRes.data || []);
                setAvailableIntervals(intervalsRes.data || []);
            } catch (error) {
                setErrorMessage(mapErrorMessage(error.message));
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    useEffect(() => {
        if (selectedFee) {
            const feeToAdd = availableFees.find(f => f.id === parseInt(selectedFee));
            if (feeToAdd) {
                 setFees(currentFees => {
                     if (!currentFees.some(f => f.fee_id === feeToAdd.id)) {
                         return [...currentFees, { fee_id: feeToAdd.id, quantity: 1, name: feeToAdd.name, amount: feeToAdd.amount }];
                     }
                     return currentFees;
                 });
            }
            setSelectedFee('');
        }
    }, [selectedFee, availableFees]);
    
    useEffect(() => {
        if (selectedDiscount) {
            const discountToAdd = availableDiscounts.find(d => d.id === parseInt(selectedDiscount));
            if (discountToAdd) {
                setDiscounts(currentDiscounts => {
                    if (!currentDiscounts.some(d => d.discount_id === discountToAdd.id)) {
                        return [...currentDiscounts, { discount_id: discountToAdd.id, name: discountToAdd.name, amount: discountToAdd.amount, type: discountToAdd.type }];
                    }
                    return currentDiscounts;
                });
            }
            setSelectedDiscount('');
        }
    }, [selectedDiscount, availableDiscounts]);

    const handleRemoveFee = (fee_id) => {
        setFees(fees.filter(f => f.fee_id !== fee_id));
    };

    const handleRemoveDiscount = (discount_id) => {
        setDiscounts(discounts.filter(d => d.discount_id !== discount_id));
    };

    const handleFeeQuantityChange = (fee_id, quantity) => {
        const quantNum = parseInt(quantity);
        if (quantNum > 0) {
            setFees(fees.map(f => f.fee_id === fee_id ? { ...f, quantity: quantNum } : f));
        }
    };
    
    const handleCreate = async () => {
        try {
            const payload = {
                name,
                first_invoice_date: firstInvoiceDate,
                interval_type: parseInt(intervalType),
                interval: parseInt(interval),
                due_days: parseInt(dueDays),
                fees: fees.map(({ fee_id, quantity }) => ({ feeId: fee_id, quantity })),
            };

            if (endDate) {
                payload.end_date = endDate;
            }

            if (discounts && discounts.length > 0) {
                payload.discounts = discounts.map(({ discount_id }) => ({ discountId: discount_id }));
            }

            if (description) {
                payload.description = description;
            }
            if (paymentLink) {
                payload.payment_link = paymentLink;
            }

            await createRecurringInvoice(studentId, payload);
            closeModal();
            refreshData();
        } catch (error) {
            const message = mapErrorMessage(error.message);
            setErrorMessage(message);
        }
    };

    if (loading) {
        return (
            <div className="modal-overlay">
                <div className="modal-content">
                    <h2>Create Recurring Invoice</h2>
                    <p>Loading...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="modal-overlay">
            <div className="modal-content">
                <h2>Create Recurring Invoice</h2>
                {errorMessage && <p className="error-message">{errorMessage}</p>}
                
                <div className="form-group">
                    <select className="input-field" value={studentId} onChange={e => setStudentId(e.target.value)}>
                        <option value="">Select Student</option>
                        {availableStudents.map(student => (
                            <option key={student.id} value={student.id}>{student.first_name} {student.last_name}</option>
                        ))}
                    </select>
                </div>

                <div className="form-group">
                    <input className="input-field" type="text" placeholder="Name" value={name} onChange={e => setName(e.target.value)} />
                </div>

                <div className="form-group">
                    <input className="input-field" type="text" placeholder="Description" value={description} onChange={e => setDescription(e.target.value)} />
                </div>
                
                <div className="form-group">
                    <label>First Invoice Date</label>
                    <input className="input-field" type="date" value={firstInvoiceDate} onChange={e => setFirstInvoiceDate(e.target.value)} />
                </div>

                <div className="form-group">
                    <label>End Date (optional)</label>
                    <input className="input-field" type="date" value={endDate} onChange={e => setEndDate(e.target.value)} />
                </div>

                <div className="form-group">
                    <select className="input-field" value={intervalType} onChange={e => setIntervalType(e.target.value)}>
                        <option value="">Select Interval Type</option>
                        {Object.entries(availableIntervals).map(([id, name]) => (
                            <option key={id} value={id}>{name}</option>
                        ))}
                    </select>
                </div>
                
                <div className="form-group">
                    <input className="input-field" type="number" placeholder="Interval" value={interval} onChange={e => setInterval(e.target.value)} />
                </div>

                <div className="form-group">
                    <input className="input-field" type="number" placeholder="Due Days" value={dueDays} onChange={e => setDueDays(e.target.value)} />
                </div>

                <div className="form-group">
                    <label>Fees</label>
                    <select className="input-field" value={selectedFee} onChange={e => setSelectedFee(e.target.value)}>
                        <option value="">Select Fee</option>
                        {availableFees.map(fee => (
                            <option key={fee.id} value={fee.id}>{fee.name} (${(fee.amount / 100).toFixed(2)})</option>
                        ))}
                    </select>
                </div>
                <div className='items-container'>
                    {fees.map(fee => (
                        <div key={fee.fee_id} className="item-row">
                            <span>{fee.name}</span>
                            <div className="item-row-actions">
                                <input className="input-field" type="number" min="1" value={fee.quantity} onChange={e => handleFeeQuantityChange(fee.fee_id, e.target.value)} />
                                <button onClick={() => handleRemoveFee(fee.fee_id)} className='delete-button text-button'>Remove</button>
                            </div>
                        </div>
                    ))}
                </div>

                <div className="form-group">
                    <label>Discounts</label>
                    <select className="input-field" value={selectedDiscount} onChange={e => setSelectedDiscount(e.target.value)}>
                        <option value="">Select Discount</option>
                        {availableDiscounts.map(discount => (
                            <option key={discount.id} value={discount.id}>
                                {discount.name} ({discount.type === 'Fixed' ? `$${(discount.amount / 100).toFixed(2)}` : `${discount.amount / 100}%`})
                            </option>
                        ))}
                    </select>
                </div>
                <div className='items-container'>
                    {discounts.map(discount => (
                        <div key={discount.discount_id} className="item-row">
                            <span>{discount.name} ({discount.type === 'Fixed' ? `$${(discount.amount / 100).toFixed(2)}` : `${discount.amount / 100}%`})</span>
                            <button onClick={() => handleRemoveDiscount(discount.discount_id)} className='delete-button text-button'>Remove</button>
                        </div>
                    ))}
                </div>
                
                <div className="form-group">
                    <input className="input-field" type="text" placeholder="Payment Link" value={paymentLink} onChange={e => setPaymentLink(e.target.value)} />
                </div>

                <div className='actions-centered'>
                    <button onClick={closeModal} className='cancel-button text-button'>Cancel</button>
                    <button onClick={handleCreate} className='submit-button text-button'>Create</button>
                </div>
            </div>
        </div>
    );
};

export default CreateRecurringInvoiceModal;
